import { useCallback, useMemo, useRef, useState } from 'react';

import { listScheduledInboxMessages } from '../services/conversationService';
import { Conversation, ScheduledInboxItem } from '../types/Conversation';
import { isExternalConversation, isRegularChannelConversation, isRegularDirectConversation } from '../utils/conversationClassification';
import { idsEqual } from '../utils/messageUtils';

interface ScheduledNavItem {
  id: string;
  conversationId: string;
  conversationTitle: string;
  content: string;
  messageType: string;
  fileName?: string;
  scheduledAtUtc: string;
}

interface SentNavItem {
  id: string;
  conversationId: string;
  conversationTitle: string;
  content: string;
  messageType: string;
  fileName?: string;
  timestamp: string;
}

interface UseConversationNavigationDataOptions {
  list: Conversation[];
  hiddenList: Conversation[];
  getConversationDisplayName: (conversation: Conversation) => string;
  onSelectConversation: (conversationId: string) => void;
  onExpandScheduledMessages: () => void;
}

interface UseConversationNavigationDataResult {
  loadingScheduledNav: boolean;
  scheduledNavItems: ScheduledNavItem[];
  sentNavItems: SentNavItem[];
  directConversationCount: number;
  channelConversationCount: number;
  externalConversationCount: number;
  hiddenConversationCount: number;
  onSelectScheduledNavItem: (conversationId: string) => void;
  onSelectSentNavItem: (conversationId: string, messageId: string) => void;
  fetchScheduledNav: () => void;
}

const sortScheduledNavItems = (items: ScheduledNavItem[]): ScheduledNavItem[] =>
  [...items].sort((a, b) => new Date(a.scheduledAtUtc).getTime() - new Date(b.scheduledAtUtc).getTime());

const toScheduledNavItem = (item: ScheduledInboxItem, conversationTitle?: string): ScheduledNavItem => ({
  id: item.id,
  conversationId: item.conversationId,
  conversationTitle: conversationTitle || item.conversationTitle || 'Conversation',
  content: item.content,
  messageType: item.messageType,
  fileName: item.fileName,
  scheduledAtUtc: item.scheduledAtUtc,
});

export const useConversationNavigationData = ({
  list,
  hiddenList,
  getConversationDisplayName,
  onSelectConversation,
  onExpandScheduledMessages,
}: UseConversationNavigationDataOptions): UseConversationNavigationDataResult => {
  const [scheduledInboxItems, setScheduledInboxItems] = useState<ScheduledInboxItem[]>([]);
  const [loadingScheduledNav, setLoadingScheduledNav] = useState(false);
  const [hasLoadedScheduledNav, setHasLoadedScheduledNav] = useState(false);
  const fetchGenerationRef = useRef(0);

  const fetchScheduledNav = useCallback(() => {
    const generation = ++fetchGenerationRef.current;
    setLoadingScheduledNav(true);

    const loadScheduledMessages = async () => {
      try {
        const items = await listScheduledInboxMessages();
        if (generation !== fetchGenerationRef.current) {
          return;
        }

        setScheduledInboxItems(items);
      } catch {
        if (generation !== fetchGenerationRef.current) {
          return;
        }
      } finally {
        if (generation === fetchGenerationRef.current) {
          setHasLoadedScheduledNav(true);
          setLoadingScheduledNav(false);
        }
      }
    };

    void loadScheduledMessages();
  }, []);

  const scheduledNavItems = useMemo(() => {
    return sortScheduledNavItems(
      scheduledInboxItems.map(item => {
        const conversation = list.find(candidate => idsEqual(candidate.id, item.conversationId));
        return toScheduledNavItem(
          item,
          conversation ? getConversationDisplayName(conversation) : item.conversationTitle,
        );
      }),
    );
  }, [getConversationDisplayName, list, scheduledInboxItems]);

  const sentNavItems = useMemo(() => {
    return list
      .flatMap(conversation => {
        const conversationTitle = getConversationDisplayName(conversation);
        return conversation.messages
          .filter(message => {
            const source = (message.source || '').toLowerCase();
            return (message.status === 'sent' || message.status === 'delivered') && source === 'internal';
          })
          .map(message => ({
            id: message.id,
            conversationId: conversation.id,
            conversationTitle,
            content: message.content,
            messageType: message.messageType,
            fileName: message.fileName,
            timestamp: message.timestamp,
          }));
      })
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [getConversationDisplayName, list]);

  const directConversationCount = useMemo(() => {
    return list.filter(conversation => isRegularDirectConversation(conversation)).length;
  }, [list]);

  const channelConversationCount = useMemo(() => {
    return list.filter(conversation => isRegularChannelConversation(conversation)).length;
  }, [list]);

  const externalConversationCount = useMemo(() => {
    return list.filter(conversation => isExternalConversation(conversation)).length;
  }, [list]);

  const hiddenConversationCount = useMemo(() => {
    return hiddenList.length;
  }, [hiddenList]);

  const onSelectScheduledNavItem = useCallback((conversationId: string) => {
    onSelectConversation(conversationId);
    onExpandScheduledMessages();
  }, [onExpandScheduledMessages, onSelectConversation]);

  const onSelectSentNavItem = useCallback((conversationId: string, messageId: string) => {
    onSelectConversation(conversationId);
    setTimeout(() => {
      const element = document.getElementById(`msg-${messageId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 400);
  }, [onSelectConversation]);

  return {
    loadingScheduledNav: !hasLoadedScheduledNav || (loadingScheduledNav && scheduledInboxItems.length === 0),
    scheduledNavItems,
    sentNavItems,
    directConversationCount,
    channelConversationCount,
    externalConversationCount,
    hiddenConversationCount,
    onSelectScheduledNavItem,
    onSelectSentNavItem,
    fetchScheduledNav,
  };
};
